import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Heart, History, Megaphone, MessageCircleQuestion, Sparkles, Video } from "lucide-react";
import { ResourceModal } from "@/pages/member/Library";
import { MiniPlanCard } from "@/components/MiniPlan";
import { LoyaltyDashCard } from "@/components/LoyaltyProgress";
import { FoundingBadge } from "@/components/FoundingBadge";
import { FoundationsBadge } from "@/components/FoundationsBadge";
import { FinishFoundationsCard } from "@/components/RootsExtras";
import { ProNudge } from "@/components/ProNudge";
import { BetaQuestionCard, BrandDocBanner, ChecklistReminder, PlanDeliveredBanner } from "@/components/DashboardAlerts";
import { WelcomeTour } from "@/components/WelcomeTour";
import { InstallPrompt, isInstalled } from "@/components/InstallPrompt";
import { ReviewPrompt } from "@/components/ReviewPrompt";
import { DailyCard, ProgressTracker } from "@/components/DailyAndProgress";
import { SeasonalIdeas } from "@/pages/member/Drops";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UsageMeter, useUsage } from "@/components/UsageMeter";
import { AIBadge, PageHeader, StatusBadge, fmtDate } from "@/components/Bits";
import { photoSrc, useSite } from "@/lib/site";

const ACTIONS = [
  { to: "/app/ask", icon: MessageCircleQuestion, title: "Ask Lindsay", body: "A marketing question on your mind?", id: "ask" },
  { to: "/app/foundations", icon: Megaphone, title: "Your Roots profile", body: "Your Roots Growth Phase and 90-day focus.", id: "foundations" },
  { to: "/app/library", icon: BookOpen, title: "Browse templates", body: "Plans, scripts, emails and videos.", id: "library" },
];

const Recent = ({ title, items, field, to, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-semibold">{title}</h3>
      <Link to={to} className="text-xs font-mono text-pinkb hover:underline">VIEW ALL</Link>
    </div>
    {items.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet.</p>}
    <ul className="divide-y divide-border">
      {items.slice(0, 4).map((it) => (
        <li key={it.id} className="py-3 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm truncate">{it[field]}</p>
            <p className="text-xs text-muted-foreground mt-1">{fmtDate(it.created_at)}</p>
          </div>
          <StatusBadge status={it.status} />
        </li>
      ))}
    </ul>
  </div>
);

const MiniList = ({ title, items, onOpen, testId, empty }) => (
  <div className="rounded-2xl border border-border bg-card p-6" data-testid={testId}>
    <h3 className="text-xl mb-4 flex items-center gap-2">{title}</h3>
    {items.length === 0 && <p className="text-sm text-muted-foreground">{empty}</p>}
    <ul className="space-y-1">
      {items.slice(0, 5).map((r) => (
        <li key={r.id}>
          <button onClick={() => onOpen(r)} data-testid={`${testId}-item-${r.id}`} className="w-full text-left flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-secondary transition-colors">
            <span className="text-sm truncate">{r.title}</span>
            <span className="text-[10px] font-mono uppercase text-muted-foreground shrink-0">{r.category}</span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const QuickTemplates = ({ resources, onOpen }) => {
  const favs = resources.filter((r) => r.favourite);
  const recent = resources.filter((r) => r.recent_rank !== null && r.recent_rank !== undefined).sort((a, b) => a.recent_rank - b.recent_rank);
  return (
    <div className="grid lg:grid-cols-2 gap-5 mb-5">
      <MiniList title={<><Heart className="h-4 w-4 text-pinkb fill-pinkb" />Your favourites</>} items={favs} onOpen={onOpen} testId="dashboard-favourites" empty="Tap the heart on any template in the library to keep it here." />
      <MiniList title={<><History className="h-4 w-4 text-aqua" />Recently used</>} items={recent} onOpen={onOpen} testId="dashboard-recent" empty="Templates you open will show up here." />
    </div>
  );
};

const KIND = { webinar: ["Live session", Video], new_asset: ["New in the library", Sparkles], news: ["News", Megaphone] };

const ThisWeek = ({ resources, onOpen }) => {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/me/this-week").then((r) => setW(r.data)).catch(() => {}); }, []);
  if (!w) return null;
  const openRes = (id) => { const r = resources.find((x) => x.id === id); if (r) onOpen(r); };
  return (
    <>
    <ChecklistReminder r={w.checklist_reminder} />
    <div className="mb-5 rounded-2xl border border-border bg-card p-6 lg:p-8" data-testid="this-week-panel">
      <div className="eyebrow mb-4">This week in the club</div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-3" data-testid="this-week-announcements">
          {w.announcements.length === 0 && <p className="text-sm text-muted-foreground">No live sessions announced yet. Check back soon.</p>}
          {w.announcements.map((a) => {
            const [label, Icon] = KIND[a.kind] || KIND.news;
            return (
              <div key={a.id} className="rounded-xl bg-secondary p-4" data-testid={`announcement-${a.id}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider text-pinkb flex items-center gap-1.5"><Icon className="h-3 w-3" />{label}{a.event_at && ` · ${new Date(a.event_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}</div>
                <div className="font-medium mt-1">{a.title}</div>
                {a.body && <p className="text-sm text-muted-foreground mt-1">{a.body}</p>}
                {a.link && <a href={a.link} target="_blank" rel="noreferrer" className="text-sm text-pinkb hover:underline mt-2 inline-block" data-testid={`announcement-link-${a.id}`}>{a.kind === "webinar" ? "Save my seat" : "Find out more"}</a>}
              </div>
            );
          })}
        </div>
        <div data-testid="this-week-new-resources">
          <div className="text-sm font-medium mb-2">New templates</div>
          {w.new_resources.length === 0 && <p className="text-sm text-muted-foreground">Nothing new in the last two weeks.</p>}
          <ul className="space-y-1">{w.new_resources.map((r) => (
            <li key={r.id}><button onClick={() => openRes(r.id)} data-testid={`this-week-resource-${r.id}`} className="text-left text-sm hover:text-pinkb transition-colors">{r.title}</button></li>
          ))}</ul>
        </div>
        <div className="space-y-2" data-testid="this-week-nudges">
          <div className="text-sm font-medium mb-2">Try this week</div>
          {w.nudges.map((n) => (
            <Link key={n.key} to={n.to} data-testid={`nudge-${n.key}`} className="flex items-start gap-2 rounded-xl border border-border p-3 text-sm hover:border-pinkb transition-colors">
              <ArrowUpRight className="h-4 w-4 text-pinkb shrink-0 mt-0.5" />{n.text}
            </Link>
          ))}
        </div>
      </div>
    </div>
    </>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [usage] = useUsage();
  const [site] = useSite();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [resources, setResources] = useState([]);
  const [open, setOpen] = useState(null);
  const [tour, setTour] = useState(false);
  const [install, setInstall] = useState(false);
  useEffect(() => { if (user && user.role !== "admin" && !user.tour_seen) setTour(true); }, [user]);

  useEffect(() => {
    api.get("/questions").then((r) => setQuestions(r.data)).catch(() => {});
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
    api.get("/survey/status").then((r) => setSurvey(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader eyebrow={`${usage?.trial ? "Free trial" : "The Roots Club"} · ${user?.business_name || (user?.audience === "cpg" ? "Your brand" : "Your venue")}`} title={`${new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 18 ? "Afternoon" : "Evening"}, ${user?.name?.split(" ")[0] || "founder"}.`}>
        <div className="flex flex-col items-start lg:items-end gap-3"><div className="flex flex-wrap gap-2"><FoundingBadge testId="dashboard-founding-badge" /><FoundationsBadge /></div><UsageMeter usage={usage} /></div>
      </PageHeader>
      <FinishFoundationsCard />
      {site?.focus_photo && (
      <div className="relative mb-10 overflow-hidden rounded-3xl h-40 sm:h-48 fade-up" data-testid="dashboard-banner">
        <img src={photoSrc(site.focus_photo.url)} alt={site.focus_photo.alt} className="absolute inset-0 w-full h-full object-cover object-[center_40%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-6 sm:px-10 text-white">
          <div className="eyebrow !text-sun mb-2">This month's focus</div>
          <p className="font-serif text-2xl sm:text-3xl max-w-md" data-testid="dashboard-focus-text">{site.focus_text}</p>
        </div>
      </div>
      )}
      <DailyCard />
      <ProgressTracker />
      <PlanDeliveredBanner />
      <BetaQuestionCard />
      <BrandDocBanner />
      <ProNudge usage={usage} />
      <LoyaltyDashCard />
      {!tour && <ReviewPrompt />}
      {survey?.show && (
        <Link to="/app/survey" data-testid="dashboard-survey-card" className="fade-up mb-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-pinkb/40 bg-pinkb/5 p-6 hover:bg-pinkb/10 transition-colors">
          <div>
            <div className="eyebrow mb-1">3 minutes · end of month</div>
            <p className="font-serif text-xl text-navy">How's the club working for you? Tell Lindsay what to keep, fix or add.</p>
          </div>
          <span className="rounded-full bg-pinkb text-white px-5 py-2 text-sm">Give feedback</span>
        </Link>
      )}
      <div className="grid md:grid-cols-3 gap-5 mb-10">
        {ACTIONS.map(({ to, icon: Icon, title, body, id }, i) => (
          <Link key={to} to={to} data-testid={`dashboard-action-${id}`} style={{ animationDelay: `${i * 90}ms` }}
            className="fade-up group rounded-2xl border border-border bg-card p-6 hover:bg-secondary hover:border-pinkb/50 transition-colors duration-300">
            <div className="flex justify-between">
              <Icon className="h-6 w-6 text-pinkb" />
              <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-pinkb transition-transform duration-300 group-hover:-translate-y-0.5" />
            </div>
            <h3 className="mt-8 text-xl font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{body}</p>
          </Link>
        ))}
      </div>
      <ThisWeek resources={resources} onOpen={setOpen} />
      <div className="mb-8" data-testid="dashboard-seasonal"><div className="flex items-center justify-between mb-3"><h2 className="text-xl font-semibold">In season now</h2><Link to="/app/drops" className="text-sm text-pinkb">See all</Link></div><SeasonalIdeas limit={2} /></div>
      <MiniPlanCard />
      <QuickTemplates resources={resources} onOpen={setOpen} />
      <ResourceModal item={open} onClose={() => setOpen(null)} />
      <div className="grid lg:grid-cols-2 gap-5">
        <Recent title="Your questions" items={questions} field="question" to="/app/ask" testId="dashboard-recent-questions" />

      </div>
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4"><AIBadge />
        <button onClick={() => setTour(true)} data-testid="replay-tour-btn" className="text-xs text-pinkb hover:underline">Replay the welcome tour</button>
        {!isInstalled() && <button onClick={() => setInstall(true)} data-testid="show-install-btn" className="ml-4 text-xs text-pinkb hover:underline">Add to your home screen</button>}
      </div>
      <WelcomeTour open={tour} onClose={() => setTour(false)} />
      <InstallPrompt auto={!!user && user.role !== "admin" && !tour} open={install} setOpen={setInstall} />
    </div>
  );
}
